import { useState, useCallback, useLayoutEffect } from 'react';
import { CloseIcon } from '@halo/ui-chat-react';
import { Box, Flex, Float, Image, Portal, VStack } from '@chakra-ui/react';
import { Avatar } from '@halo/ui-chat-react';
import { Dialog } from '@halo/ui-react';
import { tools } from '@halo/ui-chat-react';
import dayjs from 'dayjs';
import { TransferStatus, type SessionStore } from '@halo/chat-sdk-react';
import transferDetailHeaderImg from '@/assets/images/transfer/transferDetailHeader.png';
import { useWalletStore } from '@/wallet';
import { usePortalContainer } from '@/scripts/hooks';
import { DetailList } from '../DetailList';
import { StatusTitle } from './StatusTitle';

interface TransferDetailDialogProps {
  isOpen?: boolean;
  setIsOpen?: (isOpen: boolean) => void;
  sessionStore?: SessionStore;
  transferDetailsDialogData?: any;
}
export function TransferDetailDialog({
  isOpen,
  setIsOpen,
  sessionStore,
  transferDetailsDialogData,
}: TransferDetailDialogProps) {
  const { t } = useTranslation();
  const { client } = sessionStore || {};
  const { user, currentUser, message } = transferDetailsDialogData || {};
  const { payload = {} } = message || {};
  const { coinAmountDisplay } = useWalletStore();

  const { portalContainerRef } = usePortalContainer();

  const [open, setOpen] = useState(isOpen);
  const [detail, setDetail] = useState<Record<string, any>>({});
  const { copyToClipboard } = tools;

  const handleCancel = useCallback(() => {
    setOpen(false);
    setIsOpen?.(false);
  }, [setIsOpen]);

  useLayoutEffect(() => {
    setOpen(isOpen);
  }, [isOpen]);

  useEffect(() => {
    if (!payload.sendTo || !open) {
      return;
    }
    setDetail({
      ...payload,
      status:
        payload.status ||
        (user?.user_id !== currentUser?.user_id
          ? TransferStatus.Success
          : TransferStatus.Pending),
    });
    const result = client?.Api.user.resolveUsersInfo(payload.sendTo, (res) => {
      setDetail((prev) => ({ ...prev, sendToUser: res }));
    });
    return result?.removeListener;
  }, [client?.Api, currentUser?.user_id, open, payload, user?.user_id]);

  return (
    <Dialog.Root
      placement="center"
      open={open}
      onOpenChange={(details) => {
        setOpen(details.open);
        setIsOpen?.(details.open);
      }}
      lazyMount
      unmountOnExit
      restoreFocus={false}
      trapFocus={false}
      closeOnInteractOutside={false}
    >
      <Portal container={portalContainerRef}>
        <Dialog.Backdrop />
        <Dialog.Positioner closeOnInteractOutside={false}>
          <Dialog.Content
            borderRadius="2xl"
            w="440px"
            maxW="100%"
            p="0"
            overflow="hidden"
            bg="grey.10"
          >
            <Dialog.Header
              fontSize="md"
              fontWeight="semibold"
              justifyContent="space-between"
              py="0"
              px="0"
              minH="154px"
              pos="relative"
              h="352px"
            >
              <Image src={transferDetailHeaderImg} alt="img" w="full" />
              <Box
                pos="absolute"
                top="4"
                right="4"
                zIndex="1"
                justifyContent="flex-end"
                color="fg.muted"
                cursor="pointer"
                onClick={handleCancel}
              >
                <CloseIcon />
              </Box>
              <VStack pos="absolute" top="54px" left="0" w="full" gap="0">
                <Flex direction="column" alignItems="center" py="6">
                  <Box w="12" h="12" pos="relative" mx="auto">
                    <Image
                      src={detail.tokenIcon}
                      alt="info"
                      width="12"
                      height="12"
                      borderRadius="full"
                    />
                    <Float placement="bottom-end" offsetX="3" offsetY="3">
                      <Image
                        src={detail.chainIcon}
                        alt="info"
                        width="6"
                        height="6"
                        borderRadius="full"
                      />
                    </Float>
                  </Box>
                  <Box pt="3" pb="2" fontSize="xl" fontWeight="bold">
                    {detail.amount} {detail.symbol}
                  </Box>

                  <StatusTitle status={detail?.status} />
                </Flex>
              </VStack>
            </Dialog.Header>
            <Dialog.Body p="5">
              <Box className="TransactionDetails">
                <Flex
                  className="DetailCard"
                  direction="column"
                  bg="white"
                  borderRadius="lg"
                  px="1"
                  py="2"
                >
                  <DetailList
                    title="Txhash"
                    content={detail.txId || '-'}
                    onCopy={
                      detail.txId
                        ? () => {
                            copyToClipboard(detail.txId || '');
                          }
                        : undefined
                    }
                  />
                  <DetailList
                    title={
                      <Flex gap="1" alignItems="center">
                        <Box>{t('chat.fromText')}</Box>
                        <Flex
                          bg="grey.50"
                          borderRadius="xl"
                          alignItems="center"
                          px="2"
                          py="1.5"
                          overflow="hidden"
                        >
                          <Avatar
                            activeShake={false}
                            w="5"
                            h="5"
                            name={user?.name}
                            src={user?.avatar}
                            mr="1"
                          />
                          <Box fontSize="sm" fontWeight="medium" color="grey.97" truncate>
                            {user?.name}
                          </Box>
                          <Box fontWeight="medium" color="grey.92">
                            @{user?.username}
                          </Box>
                        </Flex>
                      </Flex>
                    }
                    content={detail?.from || ''}
                    onCopy={() => {
                      copyToClipboard(detail?.from || '');
                    }}
                  />
                  <DetailList
                    title={
                      <Flex gap="1" alignItems="center">
                        <Box flexShrink="0">{t('chat.sendToText')}</Box>
                        <Flex
                          bg="grey.50"
                          borderRadius="xl"
                          alignItems="center"
                          px="2"
                          py="1.5"
                          overflow="hidden"
                        >
                          <Avatar
                            activeShake={false}
                            w="5"
                            h="5"
                            name={detail?.sendToUser?.name}
                            src={detail?.sendToUser?.avatar}
                            mr="1"
                          />
                          <Box fontSize="sm" fontWeight="medium" color="grey.97" truncate>
                            {detail?.sendToUser?.name}
                          </Box>
                          <Box fontWeight="medium" color="grey.92">
                            @{detail?.sendToUser?.username}
                          </Box>
                        </Flex>
                      </Flex>
                    }
                    content={detail.to || ''}
                    onCopy={() => {
                      copyToClipboard(detail.to || '');
                    }}
                  />
                  <DetailList
                    title={t('chat.gasFeeText')}
                    content={
                      detail.txFee ? `${coinAmountDisplay(detail.txFee, detail.symbol)}` : ''
                    }
                  />
                  <DetailList title={t('chat.messageText')} content={detail.desc || ''} />
                  <DetailList
                    title={t('chat.timeText')}
                    content={
                      detail.time ? dayjs(Number(detail.time)).format('YYYY-MM-DD HH:mm:ss') : ''
                    }
                  />
                </Flex>
              </Box>
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
